import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DocumentList, DOC_KINDS } from "@/components/DocumentList";
import { uploadChunked } from "@/lib/upload";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ACCEPT = ".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp";
const UPLOAD_KINDS = Object.entries(DOC_KINDS).filter(([k]) => !["move_in", "move_out"].includes(k));

export function DocumentsDialog({ tenant, open, onOpenChange }) {
  const [docs, setDocs] = useState(null);
  const [kind, setKind] = useState("lease");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const load = () => tenant && api.get(`/tenants/${tenant.id}/documents`).then((r) => setDocs(r.data));
  useEffect(() => { if (open) { setDocs(null); load(); } }, [open, tenant?.id]); // eslint-disable-line

  const upload = async () => {
    if (!file) return toast.error("Choose a file first");
    if (file.size > 20 * 1024 * 1024) return toast.error("File must be under 20 MB");
    setProgress(2);
    try {
      const meta = await uploadChunked(file, (p) => setProgress(Math.round(p * 0.85) + 5));
      await api.post(`/tenants/${tenant.id}/documents`, { ...meta, kind, title });
      setProgress(100);
      toast.success("Document saved to tenant record");
      setFile(null); setTitle("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setTimeout(() => setProgress(null), 600); }
  };

  const remove = async (d) => {
    try { await api.delete(`/documents/${d.id}`); toast.success("Document removed"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="documents-dialog">
        <DialogHeader><DialogTitle>Documents · {tenant?.name}</DialogTitle>
          <DialogDescription>Signed leases and Section 8 paperwork stored on this tenant's record. Tenants can view these in their portal.</DialogDescription></DialogHeader>
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Type</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="doc-kind"><SelectValue /></SelectTrigger>
                <SelectContent>{UPLOAD_KINDS.map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs">Title (optional)</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="2026 lease renewal" data-testid="doc-title" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">File (PDF, Word, JPG, PNG · max 20 MB)</Label>
            <Input ref={fileRef} type="file" accept={ACCEPT} onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file" className="bg-white" /></div>
          {progress !== null && <Progress value={progress} className="h-2" data-testid="doc-progress" />}
          <Button onClick={upload} disabled={progress !== null || !file} className="w-full gap-2" data-testid="doc-upload">
            {progress !== null ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {progress !== null ? `Uploading… ${progress}%` : "Upload document"}
          </Button>
        </div>
        <div className="mt-2 max-h-[40vh] overflow-y-auto pr-1">
          {docs === null ? <p className="text-sm text-slate-400 text-center py-4">Loading…</p>
            : <DocumentList docs={docs} onDelete={remove} emptyText="No lease or Section 8 paperwork stored yet." />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
